import { useRef } from "react";
import { IonRippleEffect } from "@ionic/react";

// A plain <button> with the Ionic ripple that works outside an <IonApp>.
// Ionic only fires ion-activatable ripples through ion-app's tap handler, so
// pages like sign-in (which render outside IonApp) drive the ripple directly:
// pointer down starts it at the touch point, pointer up / leave fades it out.
export default function RippleButton({ className = "", children, disabled, onPointerDown, onPointerUp, onPointerLeave, onPointerCancel, ...props }) {
  const rippleRef = useRef(null);
  const pressed = useRef(false);
  const remover = useRef(null);

  const startedAt = useRef(0);

  // A quick tap releases within ~100ms, before the ripple has grown; hold the
  // ripple for a minimum visible time (as Ionic's own tap handler does) so a
  // tap is seen as a ripple, not a flicker.
  const MIN_VISIBLE_MS = 300;
  const end = () => {
    pressed.current = false;
    const remove = remover.current;
    if (!remove) return;
    remover.current = null;
    const wait = Math.max(0, MIN_VISIBLE_MS - (Date.now() - startedAt.current));
    setTimeout(remove, wait);
  };

  const start = (e) => {
    if (disabled || !rippleRef.current?.addRipple) return;
    pressed.current = true;
    startedAt.current = Date.now();
    rippleRef.current.addRipple(e.clientX, e.clientY).then((remove) => {
      if (pressed.current) remover.current = remove;
      else setTimeout(remove, Math.max(0, MIN_VISIBLE_MS - (Date.now() - startedAt.current)));
    }).catch(() => {});
  };

  return (
    <button
      {...props}
      disabled={disabled}
      className={`${className} ion-activatable`.trim()}
      onPointerDown={(e) => { start(e); onPointerDown?.(e); }}
      onPointerUp={(e) => { end(); onPointerUp?.(e); }}
      onPointerLeave={(e) => { end(); onPointerLeave?.(e); }}
      onPointerCancel={(e) => { end(); onPointerCancel?.(e); }}
    >
      {children}
      <IonRippleEffect ref={rippleRef} />
    </button>
  );
}
